import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject, catchError, combineLatest, map, Observable, of, shareReplay, startWith, Subscription, switchMap } from 'rxjs';
import { AssetService } from 'src/app/external-user/inventory/services/asset.service';
import { getProvision } from 'src/app/store/selectors/fleet-manager.selector';
import { selectSiteByCustomerNo } from 'src/app/store/selectors/organizations.selector';
import { State } from 'src/app/store/state';
import { InputMode, SystemType } from '../../models/deploymentForm.model';
import { System } from '../../models/device.model';
import { SystemFormMetadataService } from '../../services/system-form-metadata.service';
import { filterAndAnnotateSystems, getCanonicalSystemTypeName } from './system-select.logic';

/** 
 * Encapsulates logic for an autocomplete control that selects an available system. 
 * Form value is the system's serial number as entered in customer management.
 */
@Component({
    selector: 'app-system-select',
    templateUrl: './system-select.component.html',
    styleUrls: ['./system-select.component.scss'],
    standalone: false
})
export class SystemSelectComponent implements OnInit, OnChanges, OnDestroy {
  /** Parent site ID to filter systems on. */
  @Input() siteId: string;
  /** The parent form this control is a part of. */
  @Input() form: FormGroup;
  /** Name of the form control to bind to on the parent form. */
  @Input() controlName: string;
  /** Determines whether the backing controls are enabled/disabled. */
  @Input() disabled: boolean;
  /** The current input mode to determine validation behavior. */
  @Input() mode: InputMode;
  /** The type of system to filter on. */
  @Input() systemType: SystemType | null | undefined;
  /** Emits whenever provision status for the currently selected system changes. */
  @Output() provisionStatusChange = new EventEmitter<{ systemId: string; isProvisioned: boolean; isConflict: boolean }>();

  /** Subject to emit changes whenever the parent site is changed. */
  parentSiteSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  /** NgRx subscription to system state. */
  systemSubscription: Subscription;
  /** Observable collection of available systems. */
  systems$: Observable<any[]>;
  /** Non-observable list of the latest available systems, used for validation. */
  systems: any[];
  /** Filtered list of systems to display in autocomplete control. */
  filteredSystems$: Observable<any[]>;
  /** Full list of provisioned systems used to determine if a system has already been selected. */
  provisionedSystems: System[];
  /** NgRx subscription to provisioned system state. */
  getProvisionSubscription: Subscription;
  /** Flag to indicate whether asynchronous initialization has completed. */
  initialized: boolean = false;
  /** Ordered list of system type metadata (uppercase name + numeric systemType) derived from metadata. */
  systemTypes: { nameUpper: string; systemType: number }[] = [];

  /** The form control associated with this component. */
  get systemSelectControl() { return this.form.get(this.controlName) as FormControl<string>; }
  /** Retrieve non-dynamic system friendly name control, containing friendly name entered in customer management. */
  get friendlyNameControl() { return this.form.get('systemFriendlyName') as FormControl<string>; }

  constructor(
    public store: Store<State>,
    private readonly assetService: AssetService,
    private readonly systemFormMetadataService: SystemFormMetadataService
  ) { }

  ngOnInit(): void {
    this.systemSelectControl.addValidators(this.validateSystem().bind(this));

    this.loadSystemTypes();

    // Add friendly name as hardcoded secondary value...since we need this data but can't bind two values on the main control
    this.form.addControl('systemFriendlyName', new FormControl(''));

    this.getProvisionSubscription = this.store.select(getProvision).subscribe(devices => {
      this.provisionedSystems = devices.reduce((systems, device) => systems.concat(device.systems), []);

      // Setup observable pipeline to update the available system list whenever the parent site changes
      this.systems$ = this.parentSiteSubject.asObservable().pipe(
        switchMap(siteNumber => this.store.select(selectSiteByCustomerNo(siteNumber))),
        switchMap(site => {
          if (!site) {
            return of([]);
          }
          return this.assetService.getAssetByOrgId(site?.organizationId || '').pipe(
            map(assets => {
              assets = assets || [];

              if (!this.systemType) {
                return [];
              }

              return (assets).filter(asset => 
                asset.configurations.machineTypeID === this.systemType.toString()
              );
            }),
            catchError(() => of([]))
          );
        }),
        shareReplay(1)
      );

      // Setup observable pipeline to filter systems whenever the available systems change or the user updates the control value
      this.filteredSystems$ = combineLatest([
        this.systemSelectControl.valueChanges.pipe(startWith(this.systemSelectControl.value || '')),
        this.systems$
      ]).pipe(
        map(([controlValue, systems]) => {
          const term = (controlValue || '').toLowerCase();
          const { systems: filtered, provisionStatus } = filterAndAnnotateSystems(
            systems,
            term,
            this.provisionedSystems,
            this.systemTypes,
            this.systemType,
            this.systemSelectControl.value
          );
          if(filtered.length == 1)
            this.form.get('systemFriendlyName').setValue(filtered[0]?.name);
          // Emit outside of pure function to avoid side-effects in utility
          this.provisionStatusChange.emit(provisionStatus);
          return filtered;
        })
      );

      // Subscribe to receive latest available systems to use for validation
      this.systemSubscription = this.systems$.subscribe(systems => {
        this.systems = systems;
        this.systemSelectControl.setValue(this.systemSelectControl.value, { emitEvent: false });
      });
    });

    if (this.siteId) {
      this.parentSiteSubject.next(this.siteId);
    }

    this.initialized = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['siteId'] && (changes['siteId'].currentValue !== changes['siteId'].previousValue)) {
      if (this.initialized) {
        this.systemSelectControl.setValue('');
        this.parentSiteSubject.next(changes['siteId'].currentValue || '');
      }
    }

    if (changes['disabled']) {
      this.disabled ? this.systemSelectControl.disable() : this.systemSelectControl.enable();
    }

    // Re-validate when mode changes to update validation behavior
    if (changes['mode'] && this.initialized && this.systemSelectControl) {
      this.systemSelectControl.updateValueAndValidity();
    }
  }

  ngOnDestroy(): void {
    if (this.systemSubscription) {
      this.systemSubscription.unsubscribe();
    }

    if (this.getProvisionSubscription) {
      this.getProvisionSubscription.unsubscribe();
    }

  }

  private loadSystemTypes(): void {
    this.systemFormMetadataService.loadMetadataIfNeeded().subscribe({
      next: () => {
        this.systemTypes = this.systemFormMetadataService.getSystemTypes();
        if (this.systemSelectControl) {
          this.systemSelectControl.setValue(this.systemSelectControl.value, { emitEvent: true });
        }
      },
      error: err => console.error('[SystemSelectComponent] Failed to load system form metadata.', err)
    });
  }

  /** Display logic for textbox control. */
  displaySystem(assetId: string): string {
    return this?.systems?.find(system => system.id === assetId)?.name ?? assetId;
  }

   /** Custom validator for system form data. */
  validateSystem(): ValidatorFn {
    return (control: AbstractControl<string, string>): ValidationErrors | null => {
      const systemControlValue = control.value;

      if (!systemControlValue) {
        return null;
      }

      if (!this.systems || this.systems.length === 0) {
        return { NoOptionsError: true };
      }

      // Check if system exists in available systems
      const systemExists = this.systems.find(system => system.machineNo === systemControlValue);
      if (!systemExists) {
        return { InvalidOptionError: true };
      }

      // Skip duplicate validation in View mode (when editing existing deployments)
      if (this.mode === InputMode.View) {
        return null;
      }

      // Duplicate/conflict logic (reuses the isConflict concept from filteredSystems$):
      // Find all already provisioned systems that match selected serial (systemId/machineNo)
      const matchingSystems = this.provisionedSystems?.filter(p =>
        p.systemId?.toLowerCase() === systemControlValue?.toLowerCase()
      ) || [];

      if (matchingSystems.length > 0) {
        // Conflict if ANY matching provisioned system has a recognized system type that maps to current this.systemType
        const isConflict = matchingSystems.some(existing => {
          const existingTypeNameUpper = getCanonicalSystemTypeName(existing.systemType);
          if (!existingTypeNameUpper) { return false; }
          const systemTypeEntry = this.systemTypes.find(mt =>
            getCanonicalSystemTypeName(mt.nameUpper) === existingTypeNameUpper
          );
          return !!systemTypeEntry && this.systemType == systemTypeEntry.systemType;
        });

        // If conflict detected (already provisioned with a recognized system type), block selection
        if (isConflict) {
          return { DuplicateProvisionedError: true };
        }
      }

      return null;
    }
  }
}

